import _objectSpread from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/objectSpread2";
import _classCallCheck from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/home/runner/_work/mono/mono/node_modules/.pnpm/@babel+runtime@7.9.0/node_modules/@babel/runtime/helpers/esm/createClass";
import { passThrough, throttle } from '../utils/promise-utils';
import { path } from 'ramda';
import { axios } from './../utils/axios.js';
import { urls, setSession } from './../config.js';
import ExpiringCache from '../utils/expiring-cache';
import smsChallenge from './sms-challenge';
import * as token from './token';
var USER_MAX_CACHE = 60000;
var Auth = /*#__PURE__*/function () {
  /**
   * @param {Session} session see ./session.js for usage
   */
  function Auth(session) {
    _classCallCheck(this, Auth);
    this.session = session;
    this.challengeId = null;
    this.user = this._cachedUser();
    this.forceUserUpdate = true;
    setSession(this.session);
  }

  /**
   * Checks to see if a user is a new user who needs to accept terms and flags
   * appropriately.
   *
   * @private
   */
  return _createClass(Auth, [{
    key: "_validateUser",
    value: function _validateUser(user) {
      var accepted_terms = Boolean(user.first_name);
      return _objectSpread(_objectSpread({}, user), {}, {
        accepted_terms: accepted_terms
      });
    }

    /**
     * @private
     */
  }, {
    key: "_extractChallengeId",
    value: function _extractChallengeId(_ref) {
      var challengeId = _ref.challengeId;
      this.challengeId = challengeId;
    }

    /**
     * @private
     */
  }, {
    key: "_cachedUser",
    value: function _cachedUser() {
      return new ExpiringCache(throttle(this._fetchCurrentUser.bind(this)), this._shouldForceUserFetch.bind(this), USER_MAX_CACHE);
    }

    /**
     * @private
     */
  }, {
    key: "_shouldForceUserFetch",
    value: function _shouldForceUserFetch(current) {
      var id = current ? current.user.id : null;
      return this.forceUserUpdate || this.session.userId !== id;
    }

    /**
     * Causes the cache to update the user
     *
     * @private
     */
  }, {
    key: "_updatedUser",
    value: function _updatedUser() {
      this.forceUserUpdate = false;
    }

    /**
     * Used only by the user cache - fetches the canonical data from the server
     *
     * @private
     */
  }, {
    key: "_fetchCurrentUser",
    value: function _fetchCurrentUser() {
      var _this = this;
      if (!this.session.userToken) {
        return Promise.reject(Error('Cannot fetch user. Not logged in.'));
      }
      return axios().then(function (api) {
        return api.get(urls.apiV2('/auth/me'));
      }).then(path(['data'])).then(passThrough(this._updatedUser.bind(this))).then(function (data) {
        return {
          user: _this._validateUser(data.user),
          team: data.team
        };
      });
    }

    /**
     * When retrieving a user you should check the property accepted_terms and if
     * false prompt the user to complete their registration.
     *
     * @param {Boolean} forceFetch [default false] If set as true, will _always_
     *                             refetch from API. Otherwise may return a cached
     *                             user.
     *
     * @returns {Promise<user|Error>} Either a user object or an Error if user
     * cannot be retrieved.
     */
  }, {
    key: "getCurrentUser",
    value: function getCurrentUser() {
      var _this2 = this;
      var forceFetch = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
      this.forceUserUpdate = this.forceUserUpdate || forceFetch;
      return this.user.get().then(function (data) {
        _this2.session.updateUser({
          user_id: data.user.id,
          access_token: _this2.session.userToken,
          refresh_token: _this2.session.refreshToken,
          expires_in: _this2.session.userTokenExpiry
        });
        return data;
      });
    }
  }, {
    key: "authUsernamePassword",
    value: function authUsernamePassword(email, password) {
      return token.authPassword(email, password).then(passThrough(this.session.updateUser.bind(this.session)));
    }

    /**
     * Will try to return a user token, but if no user exists will instead return
     * a client application token
     *
     * @return {Promise<string>}
     */
  }, {
    key: "getToken",
    value: function getToken() {
      return this.session.getToken();
    }

    /**
     * Clears the current user from the session and will wipe the cached user on
     * next fetch.
     *
     * Will not affect client application tokens / auth
     */
  }, {
    key: "logout",
    value: function logout() {
      this.session.clearUser();
      this.forceUserUpdate = true;
    }

    /**
     * Starts an SMS-Challenge based login flow. Will return a promise which
     * resolves to an object containing:
     *  e164: The users number reformatted to a standard E164 number
     *  code: [dev environments only] the confirmation code, which will be sent
     *        via SMS on production systems.
     *
     * @param {string} number The users telephone number. Must be a valid UK
     *                        mobile number.
     * @returns {Promise<object>}
     */
  }, {
    key: "beginSMSChallenge",
    value: function beginSMSChallenge(number) {
      this.session.clearUser();
      return smsChallenge.begin(number).then(passThrough(this._extractChallengeId.bind(this)));
    }

    /**
     * Resolves an SMS challenge as started with `beginSMSChallenge()`. The user
     * will receive an SMS to the previously provided number and must pass this in
     * via this method in order to receive access tokens to the api.
     *
     * NOTE: The access tokens are returned from this method, but in typical usage
     * you should be able to disregard them as they are persisted via the auth
     * class itself and this should be the preferred method of acccess. (see examples)
     *
     * @param {string} code The four digit code which the user received via SMS
     * @returns {Promise<object>} Returns the API response containing access tokens
     */
  }, {
    key: "resolveSMSChallenge",
    value: function resolveSMSChallenge(code) {
      var challengeId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : this.challengeId;
      if (!challengeId) {
        return Promise.reject(new Error('Activation failed. Please try again.'));
      }
      return smsChallenge.respond(challengeId, code).then(passThrough(this.session.updateUser.bind(this.session)));
    }

    /**
     * For new users only - complete registration and accept terms and conditions
     *
     * @param {Object} user Setup first_name, surname and (optionally) email and
     *                      marketing for new users. IMPORTANT: This method will
     *                      also mark the user as having accepted huggg's terms
     *                      and conditions so ensure terms are presented.
     */
  }, {
    key: "finish",
    value: function finish(user) {
      var _this3 = this;
      return axios().then(function (api) {
        return api.post(urls.apiV2('/users/finish'), user);
      }).then(path(['data', 'user'])).then(function (user) {
        // we've updated the user object so need to invalidate our cache.
        _this3.forceUserUpdate = true;
        return _this3._validateUser(user);
      });
    }

    /**
     * If you are manually making requests to our API and receive a 401 error, you
     * can call this method to refresh any tokens. On completion you can retry
     * with the new tokens, now available through `getToken()`.
     *
     * @returns {Promise}
     */
  }, {
    key: "refreshTokens",
    value: function refreshTokens() {
      return this.session.refreshTokens();
    }

    /**
     * In general you should allow the auth module to handle all auth requirements. However, should
     * you need to manually handle refreshing tokens in certain instances you can force this module
     * back into sync with this method. This _can_ cause a mismatch between the user id and the tokens
     * in the session, and so should be used with extreme care.
     *
     * (There is no equivalent for updating client tokens as we can simply generate a new one)
     */
  }, {
    key: "updateUser",
    value: function updateUser(userId, newAccessToken, newRefreshToken, userTokenExpiry) {
      this.forceUserUpdate = true;
      this.session.updateUser({
        user_id: userId,
        access_token: newAccessToken,
        refresh_token: newRefreshToken,
        expires_in: userTokenExpiry
      });
    }

    /**
     * Authenticate user through SSO.
     *
     * @param {String} identityProvider
     * @param {String} code
     * @returns {Promise}
     */
  }, {
    key: "authSSO",
    value: function authSSO(identityProvider, code) {
      return token.authSSO(identityProvider, code).then(path(['data', 'data'])).then(passThrough(this.session.updateUser.bind(this.session)));
    }

    /**
     * Verify the identity of a person linking an SSO identity with an existing user,
     * and populate the session with tokens.
     *
     * @param {String} verificationId
     * @param {String} password
     * @returns {Promise}
     */
  }, {
    key: "verifyIdentity",
    value: function verifyIdentity(verificationId, password) {
      return axios().then(function (api) {
        return api.post(urls.apiV2("/oauth/verify-identity/".concat(verificationId)), {
          password: password
        });
      }).then(path(['data', 'data'])).then(passThrough(this.session.updateUser.bind(this.session)));
    }

    /**
     * Get a URL to authorise with an identity provider.
     *
     * @param {String} provider
     * @returns {Promise<Object>}
     */
  }, {
    key: "getIdentityProviderAuthoriseUrl",
    value: function getIdentityProviderAuthoriseUrl(provider) {
      return axios().then(function (api) {
        return api.post(urls.apiV2('/oauth/authorise/urls'), {
          provider: provider
        });
      }).then(path(['data', 'data']));
    }
  }]);
}();
export default Auth;